// «Файлы» (R-M2): all attachments the person may open, with the section and record they belong to; the section is chosen in the left
// column (FilesNav), the search by the file, the record, the form or the author is in the core's top line with the switch of the view.
// A table with thumbnails or a gallery (?view=grid); a file opens on its own page (?file=<key>, um/2026-10-30/03 п.8) with the same
// download and link (R-C16).

import { useState, type MouseEvent } from 'react';
import { ago } from '../fields/format.ts';
import { navigate, useLocation } from '../shell/router.ts';
import { Icon } from '../ui/icons.tsx';
import { TopBar, TopTitle } from '../ui/TopBar.tsx';
import { useFilesTitle } from './FilesNav.tsx';
import { copyFileLink, NONE, useFiles, type FileRow } from './files-index.ts';
import { Button, Segmented, cx, useToast } from '../ui/primitives.tsx';
import { CompactTable } from '../table/CompactTable.tsx';
import { FileThumb } from './FileThumb.tsx';
import { Gallery } from '../ui/Gallery.tsx';
import { FileView } from './FileView.tsx';
import { fileSize } from './kinds.ts';
import s from './files.module.css';

/** The address of the list with its section and view, and of a file's page from it. */
function hrefOf(params: { section?: string; view?: string; file?: string }): string {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => !!v) as [string, string][]).toString();
  return `/s/files${q ? `?${q}` : ''}`;
}

export function FilesScreen() {
  const toast = useToast();
  const { search } = useLocation();
  const section = search.get('section') ?? '';
  const view = search.get('view') === 'grid' ? 'grid' : '';
  const file = search.get('file');
  const { rows, error, truncated } = useFiles(!file);
  const [q, setQ] = useState('');
  const title = useFilesTitle();
  if (file) return <FileView fileKey={file} back={hrefOf({ section, view })} />;

  const needle = q.trim().toLowerCase();
  const shown = (rows ?? []).filter((r) => (!section || (r.typeName || NONE) === section)
    && (!needle || [r.name, r.recordTitle, r.form, r.field, r.author].some((x) => x.toLowerCase().includes(needle))));
  const open = (r: FileRow) => hrefOf({ section, view, file: r.key });
  const go = (e: MouseEvent, href: string) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(href); };
  const where = (r: FileRow) => (r.source === 'form' ? `${r.form} · ${r.field}` : `поле «${r.field}»`);

  return (
    <div className={s.wrap}>
      <TopBar start={<TopTitle icon="download">{title}</TopTitle>} search={{ value: q, onChange: setQ, placeholder: 'Файл, запись, форма или автор', label: 'Поиск по файлам', slash: true }}
        count={rows ? `${shown.length} из ${rows.length}${truncated ? ' самых новых' : ''}` : undefined}
        end={<Segmented size="sm" label="Вид" value={view === 'grid' ? 'grid' : 'table'} onChange={(v) => navigate(hrefOf({ section, view: v === 'grid' ? 'grid' : undefined }))}
          items={[{ value: 'table', label: 'Таблица', icon: 'table', iconOnly: true }, { value: 'grid', label: 'Галерея', icon: 'grid', iconOnly: true }]} />} />
      {error ? <div className={s.empty}>{error}</div> : !rows ? <div className={s.empty}>&nbsp;</div> : !rows.length ? (
        <div className={s.empty}>Файлов пока нет. Они появляются, когда в форме есть поле «Файл» и люди прикладывают слайды или документы, или когда файл кладут в карточку записи.</div>
      ) : !shown.length ? (
        <div className={s.empty}>Ничего не нашлось. <Button size="sm" variant="ghost" onClick={() => { setQ(''); navigate('/s/files'); }}>Сбросить</Button></div>
      ) : view === 'grid' ? (
        <div className={s.scroll}>
          <Gallery label="Файлы" onOpen={navigate} items={shown.map((r) => ({
            key: r.key, href: open(r), media: <FileThumb fileKey={r.key} type={r.type} name={r.name} size="lg" />,
            title: r.name, sub: [fileSize(r.size), r.recordTitle || r.form].filter(Boolean).join(' · '),
          }))} />
        </div>
      ) : (
        <div className={s.scroll}>
          <CompactTable className={s.table} rows={shown} rowKey={(r) => r.key} columns={[
            { key: 'file', label: 'Файл', render: (r) => <a className={s.fileCell} href={open(r)} onClick={(e) => go(e, open(r))}><FileThumb fileKey={r.key} type={r.type} name={r.name} /><span className={s.fileText}><span className={s.name}>{r.name}</span><span className={s.sub}>{fileSize(r.size) || ' '}</span></span></a> },
            { key: 'record', label: 'Запись', render: (r) => <>{r.recordId ? <a className={s.rec} href={`/r/${r.recordId}`} onClick={(e) => go(e, `/r/${r.recordId}`)}>{r.recordTitle || 'без названия'}</a> : NONE}<span className={s.sub}>{r.typeName}</span></> },
            { key: 'form', label: 'Откуда', render: (r) => <>{r.source === 'form' ? r.form : 'Карточка записи'}<span className={s.sub}>{r.source === 'form' ? r.field : where(r)}</span></> },
            { key: 'author', label: 'Автор', render: (r) => r.author || NONE },
            { key: 'at', label: 'Загружен', render: (r) => ago(r.at) },
            { key: 'link', label: '', className: s.actions, render: (r) => <span className={cx(s.rowActions)}><a className={s.iconLink} href={`/api/files/${r.key}`} download={r.name} aria-label="Скачать" title="Скачать"><Icon name="download" size={14} /></a><Button size="sm" icon="link" onClick={() => void copyFileLink(r, toast)}>Ссылка</Button></span> },
          ]} />
        </div>
      )}
    </div>
  );
}
